import { useState, useEffect, useRef, useCallback } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Card, CardContent } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Textarea } from "../components/ui/textarea";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "../components/ui/dialog";
import { Smartphone, Send, Loader2, Search, Trash2, ChevronLeft, Plus, Archive, FileText, Settings } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const POLL_MS = 4000;

const formatTime = (iso) => {
  if (!iso) return "";
  return new Date(iso).toLocaleString("en-US", {
    month: "short", day: "numeric", hour: "numeric", minute: "2-digit",
  });
};

const prettyNumber = (n) => {
  if (!n) return n;
  const m = /^\+1(\d{3})(\d{3})(\d{4})$/.exec(n);
  return m ? `(${m[1]}) ${m[2]}-${m[3]}` : n;
};

const STATUS_LABEL = {
  queued: "Queued", sent: "Sent", delivered: "Delivered",
  undelivered: "Undelivered", failed: "Failed", received: "",
};

const AdminSms = () => {
  const [filter, setFilter] = useState("open"); // open | archived | all
  const [search, setSearch] = useState("");
  const [list, setList] = useState([]);
  const [selectedId, setSelectedId] = useState(null);
  const [conversation, setConversation] = useState(null);
  const [messages, setMessages] = useState([]);
  const [draft, setDraft] = useState("");
  const [sending, setSending] = useState(false);

  // New message dialog
  const [newOpen, setNewOpen] = useState(false);
  const [newTo, setNewTo] = useState("");
  const [newName, setNewName] = useState("");
  const [newBody, setNewBody] = useState("");
  const [starting, setStarting] = useState(false);

  const [templates, setTemplates] = useState([]);
  const [tplMenu, setTplMenu] = useState(null); // {x, y} or null

  const lastSeqRef = useRef(0);
  const bottomRef = useRef(null);
  const sendingRef = useRef(false);
  const startingRef = useRef(false);

  const optedOut = conversation?.opted_out;

  const fetchList = useCallback(async () => {
    try {
      const params = new URLSearchParams();
      if (filter !== "all") params.set("status", filter);
      if (search.trim()) params.set("search", search.trim());
      const res = await axios.get(`${API}/admin/sms/conversations?${params.toString()}`);
      setList(res.data || []);
    } catch (e) {
      // transient
    }
  }, [filter, search]);

  useEffect(() => {
    fetchList();
    const interval = setInterval(fetchList, POLL_MS);
    return () => clearInterval(interval);
  }, [fetchList]);

  useEffect(() => {
    axios.get(`${API}/admin/sms/templates`).then((r) => setTemplates(r.data || [])).catch(() => {});
  }, []);

  useEffect(() => {
    if (!tplMenu) return;
    const close = () => setTplMenu(null);
    window.addEventListener("click", close);
    window.addEventListener("scroll", close, true);
    return () => { window.removeEventListener("click", close); window.removeEventListener("scroll", close, true); };
  }, [tplMenu]);

  const mergeMessages = useCallback((incoming) => {
    if (!incoming || incoming.length === 0) return;
    setMessages((prev) => {
      const seen = new Set(prev.map((m) => m.seq));
      const merged = [...prev];
      for (const m of incoming) if (!seen.has(m.seq)) merged.push(m);
      merged.sort((a, b) => a.seq - b.seq);
      return merged;
    });
    lastSeqRef.current = incoming.reduce((mx, m) => Math.max(mx, m.seq), lastSeqRef.current);
  }, []);

  const selectConv = async (conv) => {
    setSelectedId(conv.id);
    setConversation(conv);
    setMessages([]);
    lastSeqRef.current = 0;
    setDraft("");
    try {
      const res = await axios.get(`${API}/admin/sms/conversations/${conv.id}/messages?after_seq=0`);
      setConversation(res.data.conversation);
      mergeMessages(res.data.messages);
    } catch (e) {
      toast.error("Could not load conversation");
    }
  };

  // Poll the open conversation.
  useEffect(() => {
    if (!selectedId) return;
    const interval = setInterval(async () => {
      try {
        const res = await axios.get(`${API}/admin/sms/conversations/${selectedId}/messages?after_seq=${lastSeqRef.current}`);
        setConversation(res.data.conversation);
        mergeMessages(res.data.messages);
      } catch (e) {
        // transient
      }
    }, POLL_MS);
    return () => clearInterval(interval);
  }, [selectedId, mergeMessages]);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  const sendMessage = async () => {
    const body = draft.trim();
    if (!body || !conversation?.id || sendingRef.current) return;
    sendingRef.current = true;
    setSending(true);
    try {
      const res = await axios.post(`${API}/admin/sms/conversations/${conversation.id}/messages`, { body });
      setDraft("");
      mergeMessages([res.data]);
      fetchList();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not send text");
    } finally {
      setSending(false);
      sendingRef.current = false;
    }
  };

  const openTplMenu = (e) => {
    if (optedOut || templates.length === 0) return;
    e.preventDefault();
    setTplMenu({ x: e.clientX, y: e.clientY });
  };
  const applyTemplate = (t) => {
    setDraft((d) => (d ? d + (d.endsWith(" ") ? "" : " ") + t.body : t.body));
    setTplMenu(null);
  };

  const startConversation = async () => {
    if (startingRef.current) return;
    if (!newTo.trim() || !newBody.trim()) {
      toast.error("Enter a phone number and a message.");
      return;
    }
    startingRef.current = true;
    setStarting(true);
    try {
      const res = await axios.post(`${API}/admin/sms/conversations`, {
        to_number: newTo.trim(),
        message: newBody.trim(),
        contact_name: newName.trim() || undefined,
      });
      toast.success("Text sent");
      setNewOpen(false);
      setNewTo(""); setNewName(""); setNewBody("");
      await fetchList();
      selectConv(res.data.conversation);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not send text");
    } finally {
      setStarting(false);
      startingRef.current = false;
    }
  };

  const archiveToggle = async () => {
    if (!conversation?.id) return;
    const next = conversation.status === "archived" ? "open" : "archived";
    try {
      const res = await axios.put(`${API}/admin/sms/conversations/${conversation.id}`, { status: next });
      setConversation(res.data);
      fetchList();
    } catch (e) {
      toast.error("Could not update");
    }
  };

  const deleteConv = async (conv) => {
    if (!window.confirm(`Delete the text thread with ${conv.contact_name || prettyNumber(conv.contact_number)}? This removes all messages.`)) return;
    try {
      await axios.delete(`${API}/admin/sms/conversations/${conv.id}`);
      toast.success("Thread deleted");
      if (selectedId === conv.id) { setSelectedId(null); setConversation(null); setMessages([]); }
      setList((l) => l.filter((c) => c.id !== conv.id));
    } catch (e) {
      toast.error("Could not delete");
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); sendMessage(); }
  };

  const backToList = () => { setSelectedId(null); setConversation(null); setMessages([]); };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-3">
          <Smartphone size={24} className="text-[#f4cc1e]" />
          <h1 className="text-2xl font-bold text-white font-['Chivo']">Text Messages</h1>
        </div>
        <div className="flex items-center gap-2">
          <Link to="/admin/sms/settings"
            className="inline-flex items-center gap-1.5 h-9 px-3 rounded-md bg-[#091c3c] border border-[#1a3a6a] text-white text-sm font-medium hover:bg-[#0d2a5a]">
            <Settings size={15} /> Templates & Auto-Reply
          </Link>
          <Button onClick={() => setNewOpen(true)} className="bg-[#f4cc1e] text-[#091c3c] hover:bg-[#f4cc1e]/90 font-bold">
            <Plus size={16} className="mr-1" /> New Message
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 h-[calc(100vh-220px)] min-h-[460px]">
        {/* List */}
        <Card className={`bg-[#091c3c] border-[#091c3c] rounded-xl shadow-lg flex-col lg:col-span-1 overflow-hidden lg:flex ${selectedId ? "hidden" : "flex"}`}>
          <div className="p-3 border-b border-[#1a3a6a] space-y-2">
            <div className="relative">
              <Search size={14} className="absolute left-2 top-2.5 text-white/40" />
              <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search name or number…"
                className="pl-7 h-9 bg-[#0d2a5a] border-[#1a3a6a] text-white text-sm" />
            </div>
            <Select value={filter} onValueChange={(v) => { setFilter(v); setSelectedId(null); setConversation(null); }}>
              <SelectTrigger className="w-full h-9 bg-[#0d2a5a] border-[#1a3a6a] text-white text-sm"><SelectValue /></SelectTrigger>
              <SelectContent className="bg-[#091c3c] border-[#1a3a6a]">
                <SelectItem value="open">Open</SelectItem>
                <SelectItem value="archived">Archived</SelectItem>
                <SelectItem value="all">All</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="flex-1 overflow-y-auto">
            {list.length === 0 ? (
              <div className="p-8 text-center text-white/40 text-sm">No conversations</div>
            ) : (
              list.map((conv) => (
                <div key={conv.id} role="button" tabIndex={0}
                  onClick={() => selectConv(conv)}
                  onKeyDown={(e) => { if (e.key === "Enter") selectConv(conv); }}
                  className={`group w-full text-left px-3 py-2.5 border-b border-[#1a3a6a] hover:bg-[#0d2a5a] transition-colors cursor-pointer ${selectedId === conv.id ? "bg-[#0d2a5a]" : ""}`}
                >
                  <div className="flex items-center justify-between gap-2">
                    <span className="text-white text-sm font-medium truncate">
                      {conv.contact_name || prettyNumber(conv.contact_number)}
                    </span>
                    <div className="flex items-center gap-1.5 shrink-0">
                      {conv.admin_unread > 0 && (
                        <span className="bg-[#f4cc1e] text-[#091c3c] text-[10px] font-bold rounded-full px-1.5 py-0.5">{conv.admin_unread}</span>
                      )}
                      <button onClick={(e) => { e.stopPropagation(); deleteConv(conv); }} title="Delete" className="text-white/30 hover:text-red-400 p-0.5"><Trash2 size={14} /></button>
                    </div>
                  </div>
                  <div className="flex items-center justify-between gap-2 mt-1">
                    <span className="text-xs text-white/50 truncate">{conv.last_preview || prettyNumber(conv.contact_number)}</span>
                    <span className="text-[10px] text-white/40 shrink-0">{formatTime(conv.last_message_at)}</span>
                  </div>
                  {conv.opted_out && <Badge className="mt-1 bg-red-600/20 text-red-400 border-red-500/30">Opted out</Badge>}
                </div>
              ))
            )}
          </div>
        </Card>

        {/* Thread */}
        <Card className={`bg-[#091c3c] border-[#091c3c] rounded-xl shadow-lg flex-col lg:col-span-2 overflow-hidden lg:flex ${selectedId ? "flex" : "hidden"}`}>
          {!conversation ? (
            <div className="flex-1 flex flex-col items-center justify-center text-white/40">
              <Smartphone size={48} className="mb-4 opacity-50" />
              <p>Select a conversation</p>
            </div>
          ) : (
            <>
              <div className="px-4 py-3 border-b border-[#1a3a6a] flex items-center justify-between gap-2">
                <div className="flex items-center gap-2 min-w-0">
                  <button onClick={backToList} title="Back" className="lg:hidden shrink-0 text-white/70 hover:text-[#f4cc1e] -ml-1"><ChevronLeft size={22} /></button>
                  <div className="min-w-0">
                    <div className="text-white font-semibold truncate">{conversation.contact_name || prettyNumber(conversation.contact_number)}</div>
                    <div className="text-[11px] text-white/40">{prettyNumber(conversation.contact_number)}{conversation.status === "archived" ? " · Archived" : ""}</div>
                  </div>
                </div>
                <div className="flex items-center gap-2 shrink-0">
                  <Button onClick={archiveToggle} variant="outline" size="icon" title={conversation.status === "archived" ? "Unarchive" : "Archive"} className="border-[#1a3a6a] text-white hover:bg-[#0d2a5a] h-9 w-9"><Archive size={15} /></Button>
                  <Button onClick={() => deleteConv(conversation)} variant="outline" size="icon" title="Delete" className="border-red-500/30 text-red-400 hover:bg-red-500/10 h-9 w-9"><Trash2 size={15} /></Button>
                </div>
              </div>

              <div className="flex-1 overflow-y-auto p-4 space-y-3" onContextMenu={openTplMenu}>
                {messages.map((m) => {
                  const out = m.direction === "outbound";
                  return (
                    <div key={m.seq} className={`flex ${out ? "justify-end" : "justify-start"}`}>
                      <div className={`max-w-[78%] flex flex-col ${out ? "items-end" : "items-start"}`}>
                        <div className={`px-3 py-2 rounded-lg text-sm whitespace-pre-wrap break-words ${out ? "bg-[#f4cc1e] text-[#091c3c]" : "bg-[#0d2a5a] text-white border border-[#1a3a6a]"}`}>
                          {m.body}
                        </div>
                        <span className="text-[10px] text-white/40 mt-1 px-1">
                          {formatTime(m.created_at)}
                          {out && (
                            <span className={m.status === "failed" || m.status === "undelivered" ? "text-red-400" : ""}>
                              {" · "}{STATUS_LABEL[m.status] || m.status}{m.error_code ? ` (${m.error_code})` : ""}
                            </span>
                          )}
                        </span>
                      </div>
                    </div>
                  );
                })}
                <div ref={bottomRef} />
              </div>

              <div className="border-t border-[#1a3a6a] p-3">
                {optedOut ? (
                  <p className="text-center text-sm text-red-400/80 py-2">This contact has opted out (texted STOP). You can't send messages.</p>
                ) : (
                  <div className="flex gap-2 items-end">
                    <Textarea value={draft} onChange={(e) => setDraft(e.target.value)} onKeyDown={handleKeyDown} disabled={sending}
                      placeholder="Type a text… (right-click for templates)" rows={2}
                      className="resize-none bg-[#0d2a5a] border-[#1a3a6a] text-white placeholder:text-white/40" />
                    <Button onClick={(e) => { e.stopPropagation(); if (templates.length === 0) { toast.error("No templates yet. Add them in Templates & Auto-Reply."); return; } setTplMenu({ x: e.clientX, y: e.clientY }); }}
                      variant="outline" title="Insert a template"
                      className="border-[#1a3a6a] text-white hover:bg-[#0d2a5a] h-10 px-3">
                      <FileText size={16} />
                    </Button>
                    <Button onClick={sendMessage} disabled={sending || !draft.trim()} className="bg-[#f4cc1e] text-[#091c3c] hover:bg-[#f4cc1e]/90 h-10 px-4">
                      {sending ? <Loader2 size={16} className="animate-spin" /> : <Send size={16} />}
                    </Button>
                  </div>
                )}
              </div>
            </>
          )}
        </Card>
      </div>

      {/* New message dialog */}
      <Dialog open={newOpen} onOpenChange={setNewOpen}>
        <DialogContent className="bg-[#091c3c] border-[#1a3a6a] text-white max-w-md">
          <DialogHeader><DialogTitle className="text-white">New Text Message</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div className="space-y-1.5">
              <Label className="text-white/80">To (phone number)</Label>
              <Input value={newTo} onChange={(e) => setNewTo(e.target.value)} placeholder="(704) 555-1234"
                className="bg-[#0d2a5a] border-[#1a3a6a] text-white" />
            </div>
            <div className="space-y-1.5">
              <Label className="text-white/80">Contact name (optional)</Label>
              <Input value={newName} onChange={(e) => setNewName(e.target.value)} placeholder="e.g. John at Acme Roofing"
                className="bg-[#0d2a5a] border-[#1a3a6a] text-white" />
            </div>
            <div className="space-y-1.5">
              <div className="flex items-center justify-between gap-2">
                <Label className="text-white/80">Message</Label>
                {templates.length > 0 && (
                  <Select value="" onValueChange={(id) => {
                    const t = templates.find((x) => x.id === id);
                    if (t) setNewBody((b) => (b ? b + (b.endsWith(" ") ? "" : " ") + t.body : t.body));
                  }}>
                    <SelectTrigger className="h-8 w-auto gap-1 bg-[#0d2a5a] border-[#1a3a6a] text-white/80 text-xs">
                      <FileText size={13} className="mr-1" />
                      <SelectValue placeholder="Insert template" />
                    </SelectTrigger>
                    <SelectContent className="bg-[#091c3c] border-[#1a3a6a]">
                      {templates.map((t) => (
                        <SelectItem key={t.id} value={t.id} className="text-white">{t.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                )}
              </div>
              <Textarea value={newBody} onChange={(e) => setNewBody(e.target.value)} rows={4}
                className="resize-none bg-[#0d2a5a] border-[#1a3a6a] text-white" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setNewOpen(false)} className="text-white hover:bg-[#1a3a6a] border border-[#1a3a6a]">Cancel</Button>
            <Button onClick={startConversation} disabled={starting} className="bg-[#f4cc1e] text-[#091c3c] hover:bg-[#f4cc1e]/90 font-bold">
              {starting ? <Loader2 size={16} className="animate-spin mr-2" /> : <Send size={16} className="mr-2" />} Send
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {tplMenu && (
        <div style={{ position: "fixed", top: Math.min(tplMenu.y, window.innerHeight - 260), left: Math.min(tplMenu.x, window.innerWidth - 260) }}
          onClick={(e) => e.stopPropagation()}
          className="z-50 w-60 max-h-64 overflow-y-auto bg-[#091c3c] border border-[#1a3a6a] rounded-lg shadow-xl py-1">
          <p className="px-3 py-1.5 text-[11px] uppercase tracking-wide text-white/40">Insert template</p>
          {templates.map((t) => (
            <button key={t.id} onClick={() => applyTemplate(t)}
              className="w-full text-left px-3 py-2 hover:bg-[#0d2a5a] transition-colors">
              <p className="text-sm text-white truncate">{t.name}</p>
              <p className="text-xs text-white/40 truncate">{t.body}</p>
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

export default AdminSms;
